// SHORTAGE_CLOSE (ICT-13, last step): Fulfillment has merged the new
// material into the original Received Short receipt and told Proxy in Ace.
// Proxy logs the shortage as closed in MIC and closes the ticket, and
// Fulfillment hears that it's done.
import type { ProxyJob } from '../mockProxyJobs';
import type { TeamMessage } from '../../contexts/TeamMessagesContext';
import type {
  ProxyTask, ProxyWorkflowContext, ProxyWorkflowDefinition, ProxyPanelContext, ProxyReviewPanelSchema,
} from '../proxyWorkflowTypes';

function closedSince(job: ProxyJob, since: string): boolean {
  return job.followUpHistory.some(r => r.type === 'shortage_closed' && r.timestamp >= since);
}

function mergedMessage(job: ProxyJob, messages: TeamMessage[]): TeamMessage | undefined {
  return messages.find(m => m.kind === 'material-merged' && m.to === 'cary' && m.jobId === job.jobId);
}

function buildTasks({ jobs, teamMessages = [] }: ProxyWorkflowContext): ProxyTask[] {
  const tasks: ProxyTask[] = [];
  for (const job of jobs) {
    const message = mergedMessage(job, teamMessages);
    if (!message || closedSince(job, message.at)) continue;
    tasks.push({
      id: `shortage-close-${job.jobId}`,
      workflowType: 'SHORTAGE_CLOSE',
      jobId: job.jobId,
      title: `${job.issuer} - Close shortage`,
      subtitle: `Job ${job.jobId}`,
      reason: 'Fulfillment has merged the missing material into the original receipt, so the shortage is covered.',
      source: 'fulfillment_escalation',
      sourceLabel: 'Fulfillment notification',
      sourceDetail: {
        rows: [
          { label: 'From', value: 'Fulfillment (Receiving)' },
          ...(message.scan ? [{ label: 'Scan #', value: message.scan }] : []),
          { label: 'Message', value: message.body },
          { label: 'Sent', value: message.at },
        ],
      },
      createdAt: message.at.slice(0, 10),
      confidence: 97,
    });
  }
  return tasks;
}

function buildPanel({ job }: ProxyPanelContext): ProxyReviewPanelSchema {
  const lines = job.shortageLines || [];
  const short = lines.reduce((n, l) => n + l.shortage, 0);
  return {
    extractedFields: [
      { key: 'issuer', label: 'Issuer', display: job.issuer },
      { key: 'jobId', label: 'Job #', display: job.jobId },
      { key: 'solicitor', label: 'Solicitor', display: job.solicitor || 'Issuer (no solicitor on record)' },
      ...lines.map((l, i) => ({ key: `line-${i}`, label: l.materialType, display: `${l.shortage} were short` })),
    ],
    calculation: {
      title: 'Close the shortage',
      ruleText: 'ICT-13: the follow-up ends once the missing material has arrived.',
      rows: [{ label: 'Fulfillment', value: 'Merged the new material into the original receipt' }],
      totalRow: { label: 'Recommended action', value: 'Close the ticket', tone: 'positive' },
    },
    drafts: [],
    approveSummary: ['Log the shortage as closed in MIC', 'Tell Fulfillment it\'s closed', 'Close the ticket'],
    approveConfirmation: `Shortage closed for Job ${job.jobId}.`,
    outboundActions: [
      { key: 'mic', followUpType: 'shortage_closed', recipientEmail: 'mic@betanxt.com', subject: `Shortage closed for Job ${job.jobId}`, bodyPreview: `The ${short} short set${short === 1 ? '' : 's'} arrived and Fulfillment merged them in. Shortage closed.` },
    ],
    teamUpdate: {
      kind: 'shortage-update', title: `Shortage closed: ${job.issuer}`,
      body: `Thanks for merging the material on Job ${job.jobId}. Proxy has closed the shortage.`,
    },
    closesTicket: true,
  };
}

export const SHORTAGE_CLOSE_WORKFLOW: ProxyWorkflowDefinition = {
  type: 'SHORTAGE_CLOSE',
  label: 'Close Shortage',
  noun: 'a shortage to close',
  trigger: 'Fulfillment merges the missing material into a Received Short receipt and tells Proxy in Ace.',
  source: 'Fulfillment\'s "material merged" notification.',
  steps: ['Source & extraction', 'Close the shortage', 'Approve'],
  buildTasks,
  defaultEdits: () => ({}),
  buildPanel,
  onApprove: () => ({ materialStatus: 'Received' }),
};
